import type { ProgramId, ProgramTimetableDetails, TimetableId } from '@gyym/sdk'
import {
  type BaseReservationModalProps,
  type ModalProps,
  ReservationModal as BaseReservationModal,
} from '@gyym/ui'
import React from 'react'

import { pushContxCartUpdateEvent } from '@/lib/contx'
import { useAuthUser } from '@/modules/Auth'
import { useTimetableDetail } from '@/modules/Facilities'
import { useConsumablePoints, useMyReservations } from '@/modules/Me'

export interface ReservationModalProps extends ModalProps {
  title?: string
  isFavorited: boolean
  programId: ProgramId
  timetableId: TimetableId
  programLink: BaseReservationModalProps['programLink']
  programPageUrl: string
  programImage: string
  pointPurchaseLink: BaseReservationModalProps['pointPurchaseLink']
  shortagePointPurchaseLink: BaseReservationModalProps['shortagePointPurchaseLink']
  canPurchaseShortage: boolean
  onSubmit: (args: {
    timetable: ProgramTimetableDetails
    favoriteFacility: boolean
  }) => void
}

export const ReservationModal = ({
  title = '内容確認',
  isFavorited,
  programId,
  timetableId,
  programLink,
  programPageUrl,
  programImage,
  pointPurchaseLink,
  shortagePointPurchaseLink,
  canPurchaseShortage,
  onSubmit,
  ...props
}: ReservationModalProps) => {
  const { authUser } = useAuthUser()
  const { data: { data: reservations } = {} } = useMyReservations({
    revalidateOnMount: true,
  })
  const { data: timetable } = useTimetableDetail(programId, timetableId, {
    revalidateOnMount: true,
  })
  const { data: consumablePoints } = useConsumablePoints(
    timetable
      ? {
          useDateTime: timetable.startAt,
        }
      : undefined,
    {
      revalidateOnMount: true,
    },
  )

  // Opening the confirmation modal counts as putting the program into the
  // contx (CART RECOVERY) cart. The cart is intentionally never cleared on
  // close — leaving it behind is exactly the abandonment signal.
  const contxPushedTimetableId = React.useRef<TimetableId | null>(null)
  React.useEffect(() => {
    if (!timetable) return
    if (!authUser?.email) return
    if (contxPushedTimetableId.current === timetableId) return

    contxPushedTimetableId.current = timetableId

    pushContxCartUpdateEvent(authUser.email, [
      {
        id: String(programId),
        page: programPageUrl,
        name: timetable.program.name,
        image: programImage,
        price: String(timetable.point),
        count: '1',
      },
    ])
  }, [
    timetable,
    authUser,
    programId,
    timetableId,
    programPageUrl,
    programImage,
  ])

  const reservationModalProps =
    timetable && reservations && consumablePoints
      ? {
          facilityName: timetable.facility.name,
          programName: timetable.program.name,
          reservationDate: timetable.startAt,
          cancelDeadline: timetable.cancelDeadline,
          minuteAgo: timetable.program.minuteAgo,
          userPoints: consumablePoints.point,
          timetablePoints: timetable.point,
          hasReadAgreement: timetable.hasReadAgreement,
          facilityAgreement:
            // Cast empty agreement
            Array.isArray(timetable.facility.agreement)
              ? undefined
              : timetable.facility.agreement,
          userReservationLimit: reservations.reservationLimit,
          userCurrentReservationCount: reservations.currentReservations.length,
          overlappingReservations: timetable.overlappingReservations,
          isFavorited,
          programLink,
          pointPurchaseLink,
          shortagePointPurchaseLink,
          canPurchaseShortage,
          privacyPolicyLink: {
            href: '/policy',
            target: '_blank',
          },
          termsLink: {
            href: '/term',
            target: '_blank',
          },
          aboutPointsLink: {
            href: 'https://support.gyym.jp/hc/ja/articles/900001343663',
            target: '_blank',
            external: true,
          },
          onSubmit: ({ favoriteFacility }: { favoriteFacility: boolean }) =>
            onSubmit({ timetable, favoriteFacility }),
          isLoading: false as const,
        }
      : { isLoading: true as const }

  return (
    <BaseReservationModal {...props} title={title} {...reservationModalProps} />
  )
}
